import type {
  AIWorkflowJobResult,
  AIWorkflowState,
} from '@/components/ai-workflows/aiWorkflowStore';
import { JobWatchError } from '@/components/ai-workflows/jobWatchError';
import { useJobMonitor } from '@/components/ai-workflows/useJobMonitor';
import type { AIWorkflowJobResultWithStatus } from '@/components/ai-workflows/useWorkflowRequest';
import { useWorkflowRequest } from '@/components/ai-workflows/useWorkflowRequest';
import { handleError } from '@/utils/errorUtils';

/**
 * Pulls together the functionality required to invoke a workflow and monitor the
 * resultant job for updates. Once the job completes it updates the store with the
 * results
 * @param store To update when the job completes
 * @param statePreProcessor The function to run over the result before it is pushed to the store
 * @param workflowApiEndpoint To call to invoke the workflow
 */
export function useAIWorkflowService<TState>(
  store: AIWorkflowState<TState>,
  statePreProcessor: (
    result: AIWorkflowJobResult<TState>
  ) => AIWorkflowJobResult<TState>,
  workflowApiEndpoint: string
) {
  const { invokeWorkflow } = useWorkflowRequest<TState>();
  const { watchJob } = useJobMonitor<TState>(30_000);

  async function monitorJobProgress(
    job: AIWorkflowJobResultWithStatus<TState> | null
  ): Promise<void> {
    if (job) {
      if (job.status === 'failed') {
        store.setHasErrored();
      } else if (job.status === 'completed') {
        store.setResults(statePreProcessor(job));
      } else {
        const preProcessedResult = statePreProcessor(
          await watchJob(job.streamLocation)
        );

        store.setResults(preProcessedResult);
      }
    } else {
      // If we don't get a job back from invoking the workflow then we assume something has failed
      store.setHasErrored();
    }
  }

  /**
   * Invokes the workflow
   */
  async function runWorkflow(
    workflowBodyBuilder: () => unknown
  ): Promise<void> {
    store.jobStarted();

    try {
      const job = await invokeWorkflow(
        workflowApiEndpoint,
        workflowBodyBuilder
      );

      await monitorJobProgress(job);
    } catch (e) {
      if (e instanceof JobWatchError) {
        if (e.failureType === 'error') {
          store.setHasErrored();
        } else if (e.failureType === 'timed-out') {
          store.setHasTimedOut();
        }
      } else {
        store.setHasErrored();
      }

      handleError(e);
    }
  }

  return {
    runWorkflow,
  };
}
